import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Accordion, Badge, Button, Card, Col, Form, ListGroup, Row, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getTenantDiagnosticsAPI, retryFailedJobsForTenantAPI, searchTenantsAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';

export default function TenantSupportView() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);

  const onSearch = useCallback(async (val) => {
    setQ(val);
    if (!val.trim()) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await searchTenantsAPI(val);
      setSuggestions(res.data.items || []);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const pickTenant = async (t) => {
    setTenant(t);
    setSuggestions([]);
    setQ(`${t.tenantCode} — ${t.companyName}`);
    setLoading(true);
    try {
      const res = await getTenantDiagnosticsAPI(t.id);
      setDiag(res.data);
    } catch (e) {
      toast.error(e.message);
      setDiag(null);
    } finally {
      setLoading(false);
    }
  };

  const healthCards = useMemo(() => {
    if (!diag) return [];
    const ms = diag.integrationHealth.find((x) => x.provider === 'microsoft')?.status;
    const failed = diag.failedJobs?.length ?? 0;
    return [
      { title: 'Integrations', value: ms === 'healthy' ? 'OK' : 'Check', variant: ms === 'healthy' ? 'success' : 'warning' },
      { title: 'Failed jobs', value: String(failed), variant: failed ? 'danger' : 'success' },
      { title: 'Last booking', value: new Date(diag.lastBooking).toLocaleDateString(), variant: 'secondary' },
      { title: 'Users', value: String(diag.usersActive), variant: 'info' },
    ];
  }, [diag]);

  return (
    <div>
      <PageHeader
        title="Tenant support view"
        breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Support', path: platformUrl('support') }, { label: 'Tenant view' }]}
      />

      <div className="position-relative mb-4">
        <Form.Control
          size="lg"
          placeholder="Search tenant by name, domain, or code"
          value={q}
          onChange={(e) => onSearch(e.target.value)}
        />
        {suggestions.length > 0 && (
          <ListGroup className="position-absolute w-100 shadow-sm" style={{ zIndex: 10, maxHeight: 280, overflow: 'auto' }}>
            {suggestions.map((t) => (
              <ListGroup.Item key={t.id} action onClick={() => pickTenant(t)}>
                {t.tenantCode} — {t.companyName} <span className="text-muted small">{t.domain}</span>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </div>

      {loading && <p className="text-muted">Loading diagnostics…</p>}

      {tenant && diag && (
        <>
          <Row className="g-2 mb-4">
            {healthCards.map((c) => (
              <Col md={6} xl={3} key={c.title}>
                <Card>
                  <Card.Body className="py-2">
                    <div className="small text-muted">{c.title}</div>
                    <Badge bg={c.variant}>{c.value}</Badge>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Accordion defaultActiveKey="0" className="mb-4">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Integration status</Accordion.Header>
              <Accordion.Body>
                <Row className="g-2">
                  {diag.integrationHealth.map((h) => (
                    <Col md={4} key={h.provider}>
                      <Card>
                        <Card.Body className="py-2 text-capitalize">
                          {h.provider}: <Badge bg={h.status === 'healthy' ? 'success' : 'warning'}>{h.status}</Badge>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Recent failed jobs</Accordion.Header>
              <Accordion.Body>
                <Table size="sm">
                  <tbody>
                    {(diag.failedJobs || []).map((j) => (
                      <tr key={j.id}>
                        <td>
                          <Link to={platformUrl(`jobs/${j.id}`)}>{j.id}</Link>
                        </td>
                        <td>{j.queue}</td>
                        <td>{new Date(j.failedAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>Onboarding</Accordion.Header>
              <Accordion.Body>
                <ul className="mb-0">
                  {diag.onboarding.map((o) => (
                    <li key={o.key}>
                      {o.done ? '✓' : '○'} {o.label}
                    </li>
                  ))}
                </ul>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>Recent support actions</Accordion.Header>
              <Accordion.Body>
                <ul className="small mb-0">
                  {diag.supportActions.map((s, i) => (
                    <li key={i}>
                      {new Date(s.ts).toLocaleString()} — {s.text}
                    </li>
                  ))}
                </ul>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <div className="d-flex flex-wrap gap-2">
            <Button as={Link} to={platformUrl(`tenants/${tenant.id}/support`)}>
              Open support mode
            </Button>
            <Button variant="outline-danger" onClick={() => setRetryOpen(true)}>
              Retry all failed jobs
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => navigate(`${platformUrl('jobs')}?tenant=${encodeURIComponent(tenant.id)}`)}
            >
              View full logs
            </Button>
            <Button as={Link} variant="outline-primary" to={`${platformUrl(`tenants/${tenant.id}`)}#checklist`}>
              View onboarding
            </Button>
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={retryOpen}
        onClose={() => setRetryOpen(false)}
        onConfirm={async () => {
          try {
            await retryFailedJobsForTenantAPI(tenant.id);
            toast.success('Jobs re-queued');
            setRetryOpen(false);
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Retry all failed jobs?"
        message={`Queue retries for ${tenant?.companyName || 'tenant'}.`}
        confirmLabel="Retry"
      />
    </div>
  );
}
